// frontend/src/components/Layout/CustomerAccountMobileNav.tsx
// Mobile-first collapsible navigation for customer account pages

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Menu, 
  X, 
  ChevronDown, 
  ChevronRight,
  User
} from 'lucide-react';
import { customerAccountNavigation, customerQuickActions } from './configs/customerAccountConfig';

interface CustomerAccountMobileNavProps {
  className?: string;
}

const CustomerAccountMobileNav: React.FC<CustomerAccountMobileNavProps> = ({ 
  className = '' 
}) => {
  const location = useLocation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  
  const currentPath = location.pathname;

  // Check if current path matches navigation item
  const isActivePath = (href: string) => {
    if (href === '/account' && currentPath === '/account') {
      return true;
    }
    if (href !== '/account' && currentPath.startsWith(href)) {
      return true;
    }
    return false;
  };

  // Get current page name for header
  const getCurrentPageName = () => {
    const currentItem = customerAccountNavigation.find(item => isActivePath(item.href));
    return currentItem?.name || 'My Account';
  };

  // Get user display info
  const getUserDisplayName = () => {
    if (!user) return 'Account';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return user.username || user.email || 'Account';
  };

  const getUserInitials = () => {
    if (!user) return 'A';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) return firstName.charAt(0).toUpperCase();
    if (user.username) return user.username.charAt(0).toUpperCase();
    return 'A';
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <div className={`lg:hidden ${className}`}>
      
      {/* Mobile Header Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-lg"
        >
          <div className="flex items-center space-x-3">
            <Menu className="h-5 w-5 text-gray-400" />
            <div className="flex items-center space-x-3">
              {/* User Avatar */}
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                {user?.profile_image ? (
                  <img
                    src={user.profile_image}
                    alt={getUserDisplayName()}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-blue-600 font-semibold text-xs">
                    {getUserInitials()}
                  </span>
                )}
              </div>
              
              {/* Current Page */}
              <div>
                <span className="font-medium text-gray-900 text-sm">
                  {getCurrentPageName()}
                </span>
                <p className="text-xs text-gray-500">
                  {getUserDisplayName()}
                </p>
              </div>
            </div>
          </div>
          
          <ChevronDown 
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
          />
        </button>
        
        {/* Expanded Mobile Menu */}
        {isOpen && (
          <div className="border-t border-gray-200">
            
            {/* Quick Actions - Mobile Priority */}
            <div className="p-4 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {customerQuickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Link
                      key={action.name}
                      to={action.href}
                      onClick={handleLinkClick}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {action.name}
                        </span>
                      </div>
                      {action.badge && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {action.badge}
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Main Navigation */}
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Account
              </h3>
              <div className="space-y-1">
                {customerAccountNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActivePath(item.href);
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={handleLinkClick}
                      className={`
                        flex items-center justify-between p-3 rounded-lg transition-colors
                        ${isActive 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'text-gray-700 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon 
                          className={`h-4 w-4 ${
                            isActive ? 'text-blue-600' : 'text-gray-400'
                          }`} 
                        />
                        <div>
                          <span className="text-sm font-medium block">
                            {item.name}
                          </span>
                          {item.description && (
                            <span className="text-xs text-gray-500 block">
                              {item.description}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {item.badge && (
                          <span className={`
                            inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            ${isActive 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-red-100 text-red-800'
                            }
                          `}>
                            {item.badge}
                          </span>
                        )}
                        <ChevronRight 
                          className={`h-4 w-4 ${
                            isActive ? 'text-blue-400' : 'text-gray-400'
                          }`} 
                        />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Mobile Footer Actions */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="space-y-2">
                <Link
                  to="/services"
                  onClick={handleLinkClick}
                  className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Book a Service
                </Link>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href="/contact"
                    className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Support
                  </a>
                  <Link
                    to="/account/settings"
                    onClick={handleLinkClick}
                    className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Settings
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay for when menu is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CustomerAccountMobileNav;